import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { User } from '@/types/user'

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge for conflict resolution
 *
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export function formatFullName(user: User): string {
  const first = user.first_name?.trim() ?? ''
  const last = user.last_name?.trim() ?? ''
  if (last && first) return `${last} ${first}`
  if (last) return last
  if (first) return first
  return user.username || '—'
}

const EUR_FORMATTER = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })

/** Formats an amount given in cents as a euro string, e.g. 3900 -> "39,00 €". */
export function formatAmount(cents: number | null): string {
  if (cents == null) return '—'
  return EUR_FORMATTER.format(cents / 100)
}

/** Formats a discount given in cents as a spaced negative amount, e.g. 3900 -> "- 39,00 €". */
export function formatDiscountAmount(cents: number | null): string {
  if (cents == null) return '—'
  return `- ${formatAmount(cents)}`
}

export function formatExpiration(ts: number | null): string {
  if (ts == null) return '---'
  const date = new Date(ts * 1000)
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Formats a Unix timestamp (seconds) as a long date, e.g. "24 août 2026". */
export function formatDateLong(ts: number | null): string {
  if (ts == null) return '---'
  const date = new Date(ts * 1000)
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Copies text to clipboard. Returns a promise that resolves when the copy succeeds. */
export function copyTextToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text)
}

/** Triggers a file download with the given text content and filename. */
export function downloadTextAsFile(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
